import type { RefObject } from "react";
import React, { useState, useRef, useEffect } from "react";
import cn from "classnames";
import { useIntersectionObserver } from "usehooks-ts";
import {
  useFloating,
  useHover,
  useFocus,
  useClick,
  useInteractions,
  autoUpdate,
  arrow,
  inline as inlineFunc,
  offset,
  flip,
  shift,
  FloatingArrow,
  FloatingPortal,
  FloatingOverlay,
  useDismiss,
  safePolygon,
  FloatingFocusManager,
} from "@floating-ui/react";
import pubsub from "pubsub";
import type { IPopoverProps } from "./types";

import styles from "./Popover.module.scss";

const PopoverInner = ({
  visible,
  onVisibleChange,
  children,
  content,
  placement,
  strategy,
  disableArrow,
  arrowClassName,
  baseClassName,
  contentClassName,
  overlayStyles = {
    background: "#000",
    opacity: 0.6,
    // TODO: не сходятся из global-styles zIndex для overlay
    // вероятно нужно все zIndex переделывать с 0
    zIndex: 100,
  },
  hasMask,
  trigger,
  customCloseTriggers,
  timer,
  offset: defaultOffset = [10, 0],
  closeOnClick = true,
  padding = 0,
  portal = true,
  inline = false,
  contentTestId = "overlay__container",
  focusManagerOrder = ["content", "reference"],
  visuallyHiddenDismiss,
  disableFocusManager,
  closeOnFocusOut = true,
}: IPopoverProps) => {
  /** стейт открытия всплывающего окна */
  const [isOpen, setIsOpen] = useState<boolean>(false);

  /** реф для стрелки */
  const arrowRef = useRef(null);

  /** данные для блока и всплывающего окна + контекст всплытия для навешивания событий */
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange(isOpen) {
      setIsOpen(isOpen);
      onVisibleChange?.(isOpen);
    },
    /** Метод вызывается каждый раз, при монтировании блока и всплывающего окна, и очищается, если они изменяются.
     *  Это позволяет автоматически обновлять позицию. */
    whileElementsMounted: autoUpdate,
    /** Положение всплывающего окна относительно блока. */
    placement,
    /** Фиксированное или абсолютное позиционирование всплывающего окна */
    strategy,
    /** Настройки позиционирования */
    middleware: [
      offset({
        /** Отступ по основной оси от блока */
        mainAxis: defaultOffset[0],
        /** Отступ по пересекающей оси от блока */
        crossAxis: defaultOffset[1],
      }),
      /** Перемещает окно так чтобы оно попало во вьюпорт */
      flip(),
      /** Сдвигает на определенное значение от края вьюпорта */
      shift({ padding }),
      /** Позиционирование стрелки */
      arrow({ element: arrowRef }),
      inline ? inlineFunc() : undefined,
    ],
  });

  /** Показ окна при навердении на блок, включается если trigger не задан или равен/содержит "hover"
   * отключен если trigger содержит "none" */
  const hover = useHover(context, {
    enabled:
      !trigger ||
      trigger === "hover" ||
      (Array.isArray(trigger) &&
        trigger.includes("hover") &&
        !trigger.includes("none")),
    handleClose: safePolygon(),
  });
  /** Показ окна при фокусировании на блок, включается если trigger не задан или равен/содержит "focus"
   * отключен если trigger содержит "none" */
  const focus = useFocus(context, {
    enabled:
      !trigger ||
      trigger === "focus" ||
      (Array.isArray(trigger) &&
        trigger.includes("focus") &&
        !trigger.includes("none")),
  });
  /** Показ окна при клике по блоку, включается если trigger не задан или равен/содержит "click"
   * отключен если trigger содержит "none" */
  const click = useClick(context, {
    enabled:
      !trigger ||
      trigger === "click" ||
      (Array.isArray(trigger) &&
        trigger.includes("click") &&
        !trigger.includes("none")),
    /** При повторном клике на блок, закрыть окно, при условии
     * - что не задан customCloseTriggers или он не содержит "clickOnContent" */
    toggle:
      !customCloseTriggers || !customCloseTriggers.includes("clickOnContent"),
  });
  /** Закрытие окна.
   * - По нажатию Esc
   * - По клику снаружи блока всегда */
  const dismiss = useDismiss(context, {
    enabled: true,
    escapeKey: true,
    referencePress: false,
    outsidePress: true,
  });
  /** исходя из настроек взаимодействия с всплывающим окном формируются пропсы для блока и окна */
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    click,
    dismiss,
  ]);

  /** контент всплывающего окна */
  const floating = (
    <FloatingFocusManager
      context={context}
      order={focusManagerOrder}
      closeOnFocusOut={closeOnFocusOut}
      modal={false}
      initialFocus={-1}
      visuallyHiddenDismiss={visuallyHiddenDismiss}
      disabled={disableFocusManager}
    >
      <>
        {hasMask && (
          /** оверлей для всплывающего окна */
          <FloatingOverlay
            style={overlayStyles}
            onClick={() => {
              setIsOpen(false);
            }}
            lockScroll
          />
        )}
        <div
          ref={refs.setFloating}
          style={floatingStyles}
          className={cn(styles.popover, contentClassName)}
          data-testid={contentTestId}
          {...getFloatingProps({
            onClick: () => {
              /** По клику на контент всплывающего окна оно закроется, если
               * - не переданы customCloseTriggers
               * - customCloseTriggers содержит "clickOnContent" или "clickOnAnywhere"
               * - closeOnClick не false */
              if (
                !customCloseTriggers ||
                customCloseTriggers?.includes("clickOnContent") ||
                customCloseTriggers?.includes("clickOnAnywhere") ||
                closeOnClick
              ) {
                setIsOpen(false);
                onVisibleChange?.(false);
              }
            },
          })}
        >
          <div>{content}</div>
          {!disableArrow && (
            /** стрелка */
            <FloatingArrow
              ref={arrowRef}
              context={context}
              fill="white"
              data-testid="popover__arrow"
              className={arrowClassName}
            />
          )}
        </div>
      </>
    </FloatingFocusManager>
  );

  const entry = useIntersectionObserver(
    refs.reference as RefObject<Element>,
    {}
  );
  const isIntersecting = entry?.isIntersecting;

  useEffect(() => {
    if (
      (!customCloseTriggers || customCloseTriggers.includes("lostIntersect")) &&
      !isIntersecting &&
      isOpen
    ) {
      /** скрывать окно, если родительский блок скрылся из области видимости */
      setIsOpen(false);
    }
  }, [isIntersecting]);

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null;
    if (timer && isOpen) {
      /** если задан таймер закрытия окна ставим таймаут */
      timeoutId = setTimeout(() => {
        setIsOpen(false);
      }, timer);
    }
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timer, isOpen]);

  useEffect(() => {
    if (visible) {
      /** если передано свойство visible из родителя, открывается всплывашка */
      setIsOpen(true);
    }
    if (visible === false && isOpen) {
      /** закрываем */
      setIsOpen(false);
    }
  }, [visible]);

  useEffect(() => {
    if (isOpen) {
      pubsub.publish("helpdeskeddy-hide-widget");
    } else {
      pubsub.publish("helpdeskeddy-show-widget");
    }
  }, [isOpen]);

  return (
    <>
      <div
        /** добавляются нужные пропсы для основного блока */
        ref={refs.setReference}
        data-testid="popover__baseElement"
        className={baseClassName}
        style={inline ? { display: "inline-block" } : {}}
        {...getReferenceProps()}
      >
        {React.isValidElement(children) &&
          React.cloneElement(children, {
            // @ts-ignore
            "aria-expanded": isOpen,
          })}
      </div>
      {isOpen && (
        <>
          {portal ? (
            /** всплывающее окно крепится к body */
            <FloatingPortal>{floating}</FloatingPortal>
          ) : (
            floating
          )}
        </>
      )}
    </>
  );
};

export default PopoverInner;

PopoverInner.displayName = "PopoverInner";
